<template>
  <div
    ref="clusterRef"
    style="display: none;"
  />
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onBeforeUnmount, nextTick, PropType, watch } from 'vue';
import L, { Marker, Point, MarkerOptions } from 'leaflet';
import type { Map as LeafletMap } from 'leaflet';
import 'leaflet.markercluster';
import { MarkerStatus } from '@/util/station';
import type { Station } from '@/stores/station/station-model';
import StationDatasetStore from '@/stores/dataset/station-dataset-module';
import DatasetStore from '@/stores/dataset/dataset-module';
import StationStore from '@/stores/station/station-module';
import { localeDateString, getDisplayFrequency } from '@/util/datetime';
import { useI18n } from "vue-i18n";
import blueMarker from '@/assets/pin/marker-midnight.png';
import lightBlueMarker from '@/assets/pin/marker-lime.png';
import greyMarker from '@/assets/pin/marker-grey.png';
import markerShadow from '@/assets/pin/marker-shadow.png';

type MarkerWithStatus = Marker & {
  options: MarkerOptions;
  status: MarkerStatus;
  station: Station;
};

const ICONS: Record<MarkerStatus, L.Icon> = {
  'active': L.icon({
    iconUrl: blueMarker,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: markerShadow,
    shadowSize: [41, 41],
  }),
  'previously-active': L.icon({
    iconUrl: lightBlueMarker,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: markerShadow,
    shadowSize: [41, 41],
  }),
  'inactive': L.icon({
    iconUrl: greyMarker,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: markerShadow,
    shadowSize: [41, 41],
  }),
};

export default defineComponent({
  name: 'StationMarkerCluster',
  props: {
    map: { type: Object as PropType<LeafletMap>, required: true },
    stationsWithStatus: {
      type: Array as () => { station: Station; status: MarkerStatus }[],
      required: true,
    },
    maxClusterRadius: { type: Number, default: 0.001 },
  },
  setup(props) {
    const { t, locale } = useI18n();
    const clusterRef = ref<HTMLDivElement | null>(null);
    const clusterGroup = ref<L.MarkerClusterGroup | null>(null);

    const stationDatasetStore = StationDatasetStore;
    const datasetStore = DatasetStore;
    const stationStore = StationStore;

    const markersMap = ref<Map<string, MarkerWithStatus>>(new Map());

    const iconCreateFunction = (cluster: L.MarkerCluster) => {
      const childMarkers = cluster.getAllChildMarkers() as MarkerWithStatus[];
      const childCount = cluster.getChildCount();

      const activeCount = childMarkers.filter((m) => m.status === 'active').length;
      const previouslyActiveCount = childMarkers.filter((m) => m.status === 'previously-active').length;

      let className = 'marker-cluster';
      let countPrepend = '';

      if (activeCount && previouslyActiveCount) {
        console.warn('Cluster contains both active and previously-active markers.');
      } else if (activeCount) {
        className += ' active';
        countPrepend = activeCount + '/';
      } else if (previouslyActiveCount) {
        className += ' previously-active';
        countPrepend = previouslyActiveCount + '/';
      } else {
        className += ' inactive';
      }

      return new L.DivIcon({
        html: `<div><span>${countPrepend}${childCount}</span></div>`,
        className,
        iconSize: new Point(40, 40),
      });
    };

    const getLatLng = (station: Station) => L.latLng(station.lat, station.lon);

    const cleanDate = (date: Date | undefined) => {
      if (!date || !datasetStore.metadata) return '';
      return localeDateString(date, locale.value, getDisplayFrequency(datasetStore.metadata.frequency));
    };

    const getTooltipHtml = (station: Station) => {
      const start = cleanDate(station.startDate);
      const end = cleanDate(station.endDate);
      return `
        ${station.name} (${stationDatasetStore.metadata?.idType}-${t(
            'station_table.table.id'
          )}: ${station.id})<br>
        ${start} - ${end}<br>
        ${station.altitude}m
      `;
    };

    const createMarker = ({ station, status }: { station: Station; status: MarkerStatus }): MarkerWithStatus => {
      const marker = L.marker(getLatLng(station)) as MarkerWithStatus;
      marker.status = status;
      marker.station = station;

      marker.setIcon(ICONS[status]);

      marker.on('click', () => {
        stationStore.toggleStationIsSelected(station);
      });
      let hoverTimeout: number;

      marker.on('mouseover', () => {
        hoverTimeout = window.setTimeout(() => {
          if (!marker.getPopup()) {
            marker.bindPopup(getTooltipHtml(station), {
              className: 'station-marker-tooltip',
              closeButton: false,
              autoClose: false,
              autoPan: false,
            });
          }
          marker.openPopup();
        }, 150);
      });

      marker.on('mouseout', () => {
        clearTimeout(hoverTimeout);
        marker.closePopup();
      });

      return marker;
    };

    const refreshMarkers = () => {
      if (!clusterGroup.value) return;

      const removedMarkers: MarkerWithStatus[] = [];
      const changedMarkers: MarkerWithStatus[] = [];
      const addedMarkers: MarkerWithStatus[] = [];
      const currentIds = new Set(props.stationsWithStatus.map(s => s.station.id));

      // Remove markers no longer present
      markersMap.value.forEach((marker, id) => {
        if (!currentIds.has(id)) {
          removedMarkers.push(marker as MarkerWithStatus);
          markersMap.value.delete(id);
        }
      });
      if (removedMarkers.length > 0) {
        clusterGroup.value.removeLayers(removedMarkers);
      }

      // Add or update markers
      props.stationsWithStatus.forEach(({ station, status }) => {
        let marker = markersMap.value.get(station.id);

        if (marker) {
          if (marker.status !== status) {
            marker.status = status;
            marker.setIcon(ICONS[status]);
            changedMarkers.push(marker as MarkerWithStatus);
          }
        } else {
          marker = createMarker({ station, status });
          addedMarkers.push(marker as MarkerWithStatus);
          markersMap.value.set(station.id, marker);
        }
      });

      if (addedMarkers.length > 0) {
        clusterGroup.value.addLayers(addedMarkers);
      }
      if (changedMarkers.length > 0) {
        clusterGroup.value.refreshClusters(changedMarkers);
      }
    };

    onMounted(() => {
      if (!props.map) {
        console.log("map not ready");
        return;
      }

      clusterGroup.value = L.markerClusterGroup({
        maxClusterRadius: props.maxClusterRadius,
        iconCreateFunction,
      });

      props.map.addLayer(clusterGroup.value as L.MarkerClusterGroup);

      nextTick(() => {
        refreshMarkers();
      });
    });

    onBeforeUnmount(() => {
      clusterGroup.value?.clearLayers();
      markersMap.value.clear();
    });

    watch(() => props.stationsWithStatus, () => {
      refreshMarkers();
    });

    return { clusterRef };
  },
});
</script>

<style>
/* TODO: this is unscoped CSS, how to solve this? */
/*it works scoped when css is moved to StationMap component, because apparently the icons are child components of the map and not the cluster itself*/

@import '~leaflet/dist/leaflet.css';
@import '~leaflet.markercluster/dist/MarkerCluster.css';
@import '~leaflet.markercluster/dist/MarkerCluster.Default.css';

.marker-cluster.active {
  /*outer ring*/
  color: white;
  background-color: rgb(80, 106, 112, 0.42);
}

.marker-cluster.active div {
  /*inner ring*/
  color: white;
  font-weight: 600;
  background-color: rgb(5, 46, 55, 0.68);
}

.marker-cluster.previously-active {
  /*outer ring*/
  color: rgb(var(--v-theme-primary));
  background-color: rgb(212, 222, 125, 0.42);
}

.marker-cluster.previously-active div {
  /*inner ring*/
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
  background-color: rgb(191, 206, 64, 0.68);
}

.marker-cluster.inactive {
  /*outer ring*/
  background-color: rgb(140, 140, 140, 0.42);
}

.marker-cluster.inactive div {
  /*inner ring*/
  color: white;
  font-weight: 600;
  background-color: rgb(120, 120, 120, 0.68);
}
</style>
